import React, { useEffect, useState } from 'react';
import { Row, Badge } from '../ui-kit';
import {
  IconSpinner,
  IconSearch,
  IconShieldHalved,
  IconHandshake,
  IconPager,
  IconEnvelope,
  IconMobile
} from '../ui-kit/icons';
import axios from 'axios';
import './SearchBar.css';

const icon = {
  'shield-halved': <IconShieldHalved />,
  'handshake-o': <IconHandshake />,
  handshake: <IconHandshake />,
  pager: <IconPager />,
  envelope: <IconEnvelope />,
  mobile: <IconMobile />
};

const renderMatchContent = content => {
  // the part that matches the query is surrounded with with <?> </?>
  const [before, rest] = content.split('<?>');
  const [match, after] = rest.split('</?>');

  return (
    <>
      {before}
      <span style={{ color: 'black', backgroundColor: 'yellow' }}>{match}</span>
      {after}
    </>
  );
};

const renderField = field => {
  // no need to show ID if there is no match on it
  if (field.name == 'id' && !field.values[0].match) return;

  const makeLink = (url, content) => (url ? <a href={url}>{content}</a> : content);

  // comma separated list of values with optional link
  const fieldValues = field.values.map((value, index) => [
    index > 0 && ', ',
    makeLink(value.url, value.match ? renderMatchContent(value.match_content) : value.value)
  ]);

  return (
    <span key={field.name} className="me-3">
      <b>{field.name_i18n}:</b> {fieldValues}
    </span>
  );
};

const ActionButtons = ({ result }) => (
  <div className="ms-3 action-buttons">
    {result.action_links.map((link, index) => (
      <a key={index} href={link.url} className="me-2" target="_blank" title={link.title} rel="noreferrer">
        {icon[link.icon]}
      </a>
    ))}
  </div>
);

const SearchBar = ({ query }) => {
  const [searchResults, setSearchResults] = useState([]);
  const [queryValue, setQueryValue] = useState(query || '');
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (queryValue.length === 0) {
      setSearching(false);
      setSearchResults([]);
      return;
    }

    const controller = new AbortController();
    const search = async (retry = 0) => {
      setSearching(true);

      axios
        .request({
          url: `/api/v2/search?q=${queryValue}`,
          method: 'GET',
          timeout: 5000,
          signal: controller.signal
        })
        .then(response => {
          setSearchResults(response.data);
          setSearching(false);
        })
        .catch(error => {
          const shouldRetry = error.response && error.response.status >= 500;
          if (axios.isCancel(error)) {
            console.log('Request canceled', error.message);
          } else if (shouldRetry && retry < 3) {
            setTimeout(() => search(retry + 1), 200); // try again only if not cancelled
          } else {
            setSearching(false);
          }
        });
    };

    search();

    return () => controller.abort();
  }, [queryValue]);

  const onChange = event => {
    const query = event.target.value;

    const url = new URL(globalThis.location.href);
    url.searchParams.set('q', query);
    globalThis.history.replaceState({}, '', url.toString());

    setQueryValue(query);
  };

  return (
    <div className="search-bar">
      <Row className="mb-5">
        <div className="col-12">
          <div className="input-group">
            <span className="input-group-text">
              {!searching && <IconSearch fixedWidth />}
              {searching && <IconSpinner spin fixedWidth />}
            </span>
            <input
              autoFocus
              className="form-control form-control-lg"
              placeholder={I18n.t('system_wide_search.placeholder')}
              type="text"
              onChange={onChange}
              value={queryValue}
            />
          </div>
        </div>
      </Row>
      <Row>
        <div className="col-12">
          {!searching && queryValue.length > 0 && searchResults.length === 0 && I18n.t('no_results_found')}
          {searchResults.map(result => (
            <div className="mb-4 search-result" key={`${result.type}_${result.id}`}>
              <h4>
                <a href={result.url}>
                  <Badge variant="secondary">{result.type_i18n}</Badge> {result.title}
                </a>
                <ActionButtons result={result} />
              </h4>
              {result.fields.map(f => renderField(f))}
            </div>
          ))}
        </div>
      </Row>
    </div>
  );
};

export default SearchBar;
